/* eslint-disable react-hooks/exhaustive-deps */
import queryString from 'query-string';
import { useCallback } from 'react';
import { useNavigate, type NavigateOptions } from 'react-router-dom';

import queryStringOptions from '@/constants/queryStringOptions';
import { AchievementOverview } from '@/types/achievement';
import { ENCOUNTER_TYPES_API, EncounterSummary } from '@/types/encounter';
import { ExternalPolicyVerification } from '@/types/externalVerification';
import { InsurancePolicy } from '@/types/insurancePolicy';
import type { Item } from '@/types/item';
import type { Person } from '@/types/person';
import encounterTypeFromMapping from '@/utils/encounterTypeFromMapping';
import formatPersonName from '@/utils/formatPersonName';

const useNavigateToPage = () => {
  const navigate = useNavigate();

  const navigateToAppointments = useCallback(
    (personId: string) => navigate(`/people/${personId}/appointments`),
    []
  );

  const navigateToBooking = useCallback((personId: string, params?: URLSearchParams) => {
    const url = `/people/${personId}/booking${params ? `?${params.toString()}` : ''}`;
    navigate(url);
  }, []);

  const navigateToAppointment = useCallback(
    (personId: string, appointmentId: string) =>
      navigate(`/people/${personId}/appointments/${appointmentId}`),
    []
  );

  const navigateToAppointmentById = useCallback(
    (appointmentId: string, options?: NavigateOptions) =>
      navigate(`/appointments/${appointmentId}`, options),
    []
  );

  const navigateToChromeExtensionRoot = useCallback(() => navigate('/?chrome-extension=true'), []);

  const navigateToDrugScreen = useCallback(
    (drugScreenId: string, personId: string) =>
      navigate(`/people/${personId}/drug-screens/${drugScreenId}`),
    []
  );

  const navigateToProfile = useCallback(
    (personData: Person) => navigate(`/people/${personData.id}`, { state: { person: personData } }),
    []
  );

  const navigateToProfileWithId = useCallback((id: string) => navigate(`/people/${id}`), []);

  const navigateToRewardCards = useCallback(
    (id: string) => navigate(`/people/${id}/reward-cards`),
    []
  );

  const navigateToScheduling = useCallback(
    (personData: Person, providerId: string) =>
      navigate(`/people/${personData.id}/scheduling`, {
        state: { person: personData, providerId: providerId }
      }),
    []
  );

  const navigateToSchedulingWithId = useCallback(
    (personId: string, params?: URLSearchParams) =>
      navigate(`/people/${personId}/scheduling${params ? `?${params}` : ''}`),
    []
  );

  const navigateToCms1500 = useCallback(
    (id: string) => navigate(`/billing/claims/by_healthie_id/${id}/`),
    []
  );
  const navigateToCms1500ByCandidEncounterId = useCallback(
    (id: string) => navigate(`/billing/claims/by_candid_encounter_id/${id}/`),
    []
  );
  const navgiateToBillingDashboard = useCallback(() => navigate('/billing'), []);
  const navigateToNewRemit = useCallback(() => navigate('/billing/remit/new'), []);
  const navigateToEditRemit = useCallback(
    (id: string) => navigate(`/billing/remit/${id}/edit`),
    []
  );
  const navigateToEditRemitDetails = useCallback(
    (id: string) => navigate(`/billing/remit/${id}/edit/details`),
    []
  );
  const navigateToRemits = useCallback(() => navigate('/billing/remits'), []);

  const navigateToTransactions = useCallback(
    (id: string) => navigate(`/people/${id}/transactions`),
    []
  );

  const navigateToNewNote = useCallback(
    (params: { objectId: string; returnTo: string; contentTypeName: string }) => {
      const newNoteParams = queryString.stringify(params, queryStringOptions);
      return navigate(`/notes/new?${newNoteParams}`);
    },
    []
  );
  const navigateToNewReferral = useCallback(
    (personId: string) => navigate(`/referrals/new?personId=${personId}`),
    []
  );

  const navigateToEditReferral = useCallback(
    (referralId: string) => navigate(`/referrals/${referralId}/edit`),
    []
  );

  const navigateToTask = useCallback(
    (taskData: Item) =>
      navigate(`/people/${taskData.person_id}/tasks/${taskData.id}`, { state: { task: taskData } }),
    []
  );

  const navigateToNewTask = useCallback(
    (personId: string) => navigate(`/people/${personId}/tasks/new`),
    []
  );

  const navigateToEncounters = useCallback(
    ({
      encounterType,
      encounterHealthieId
    }: {
      encounterType: string;
      encounterHealthieId: string;
    }) => navigate(`/encounters/${encounterType}/by_healthie_id/${encounterHealthieId}`),
    []
  );

  const navigateToEncounterDetails = useCallback(
    (encounter: EncounterSummary, { replace }: Pick<NavigateOptions, 'replace'> = {}) => {
      const { encounter_type, person, encounter_id, healthie_id } = encounter;
      const encounterType = encounterTypeFromMapping(encounter_type);

      if (!encounterType) {
        throw new Error('Invalid encounter type');
      } else {
        const { label, path } = encounterType;
        const encounterTypeTitle = label ?? encounter_type;

        if (encounter_type === ENCOUNTER_TYPES_API.APPOINTMENT) {
          // Appointments do not have detail views as they are not billable
          return navigateToEncounters({
            encounterType: ENCOUNTER_TYPES_API.APPOINTMENT,
            encounterHealthieId: String(healthie_id)
          });
        }

        return navigate(`/encounters/${encounterType.path}/by_id/${encounter_id}/`, {
          replace,
          state: {
            headerName:
              (person && formatPersonName({ person: person as Person })) ?? encounterTypeTitle,
            path,
            person
          }
        });
      }
    },
    []
  );

  const navigateToEncounterWithoutPerson = useCallback(
    (encounterType: string, encounterId: string) => {
      return navigate(`/encounters/${encounterType}/by_id/${encounterId}`, {
        state: {
          headerName: 'Encounter' as string,
          person: null
        }
      });
    },
    []
  );

  const navigateToEncounterBillingWithoutPerson = useCallback(
    (encounterType: string, encounterId: string) => {
      return navigate(`/encounters/${encounterType}/by_id/${encounterId}/billing`, {
        state: {
          headerName: 'Encounter' as string,
          person: null
        }
      });
    },
    []
  );

  const navigateToInsuranceVOBHistory = useCallback(
    (personId: string, insurancePolicy: InsurancePolicy) => {
      return navigate(`/people/${personId}/insurance/${insurancePolicy.id}/vob-history`, {
        state: {
          insurancePolicy: insurancePolicy
        }
      });
    },
    []
  );

  const navigateToCreatePersonAchievement = useCallback(
    (personId: string, achievementsOverview: AchievementOverview[]) => {
      return navigate(`/people/${personId}/person-achievements`, {
        state: {
          achievements: achievementsOverview
        }
      });
    },
    []
  );

  const navigateToVOBList = useCallback(
    (params?: {
      member_id?: string;
      policy_id?: string;
      is_most_recent_per_policy?: boolean;
      is_treatable?: boolean;
      is_active_member?: boolean;
      is_active_policy?: boolean;
      vob_stale_days?: number;
    }) => {
      const search = params ? queryString.stringify(params, queryStringOptions) : '';

      return navigate(`/vob/list${search ? `?${search}` : ''}`);
    },
    []
  );

  const navigateToNewInsuranceFromDiscovered = useCallback(
    (
      personId: string,
      externalPolicyVerification: ExternalPolicyVerification,
      insuranceId: string,
      insurancePolicyPriority?: string | null
    ) => {
      return navigate(`/people/${personId}/insurance/${insuranceId}/new-from-discovered`, {
        state: {
          externalPolicyVerification,
          insurancePolicyPriority
        }
      });
    },
    []
  );

  const navigateToActivityLog = useCallback((formAnswerGroupId: string) => {
    return navigate(`activity-logs/by-form-answer-group-id/${formAnswerGroupId}/`);
  }, []);

  const navigateToSettings = useCallback(() => {
    return navigate('/settings');
  }, []);

  const navigateToNewROI = useCallback(() => navigate(`./new-roi`), []);

  return {
    navigateToActivityLog,
    navigateToAppointment,
    navigateToAppointmentById,
    navigateToAppointments,
    navigateToChromeExtensionRoot,
    navigateToCms1500,
    navigateToCms1500ByCandidEncounterId,
    navigateToCreatePersonAchievement,
    navgiateToBillingDashboard,
    navigateToNewRemit,
    navigateToNewNote,
    navigateToEditRemit,
    navigateToEditRemitDetails,
    navigateToRemits,
    navigateToDrugScreen,
    navigateToEncounterDetails,
    navigateToEncounterWithoutPerson,
    navigateToEncounterBillingWithoutPerson,
    navigateToEncounters,
    navigateToProfile,
    navigateToProfileWithId,
    navigateToRewardCards,
    navigateToTask,
    navigateToNewTask,
    navigateToTransactions,
    navigateToNewReferral,
    navigateToEditReferral,
    navigateToInsuranceVOBHistory,
    navigateToVOBList,
    navigateToNewInsuranceFromDiscovered,
    navigateToScheduling,
    navigateToSchedulingWithId,
    navigateToSettings,
    navigateToNewROI,
    navigateToBooking
  };
};

export default useNavigateToPage;
